import * as React from "react";
import { Loader2, Radio, WifiOff } from "lucide-react";
import { TextInput } from "../../../../ui/components/Form";
import { Button } from "../../../../ui/components/Button";
import { useI18n } from "../../../lib/i18n";
import type {
  CommandExecution,
  ProductCommand,
  ProductCommandsResponse,
} from "../../../lib/api/commands";
import { CommandExecutionModal } from "../../commands/CommandExecutionModal";
import {
  CommandDangerPill,
  CommandStatusPill,
  commandSourceLabel,
  formatCommandTime,
  useRelativeElapsed,
} from "../../commands/CommandPills";
import * as s from "../../commands/commands.css";
import { CommandSendModal } from "./CommandSendModal";
import { useProductCommandExecutions } from "./useProductCommands";

const ALL = "__all__";

function disabledReason(
  t: (key: string, values?: Record<string, string>) => string,
  command: ProductCommand,
  data: ProductCommandsResponse,
  inFlight: CommandExecution | null,
): string | null {
  if (!data.device.connected) return t("pro.commands.section.notConnected");
  if (!command.available) return command.unavailableReason ?? t("pro.commands.section.unavailable");
  if (!data.access.canSend) return t("pro.commands.section.noSendPermission");
  if (command.dangerLevel !== "normal" && !data.access.canSendDisruptive) {
    return t("pro.commands.section.noDisruptivePermission");
  }
  if (inFlight) return t("pro.commands.section.busy", { command: inFlight.displayName ?? inFlight.commandKey });
  return null;
}

function InFlightBanner({ execution, onOpen }: { execution: CommandExecution; onOpen: () => void }) {
  const { t } = useI18n();
  const elapsed = useRelativeElapsed(execution.sentAt ?? execution.createdAt, true);
  return (
    <div className={s.banner} role="status">
      <Loader2 size={16} className={s.spinner} />
      <span style={{ flex: 1 }}>
        {t(
          execution.phase === "pre_read" ? "pro.commands.section.inFlightPreRead" : "pro.commands.section.inFlight",
          { command: execution.displayName ?? execution.commandKey, seconds: elapsed },
        )}
      </span>
      <button type="button" className={s.linkButton} onClick={onOpen}>
        {t("pro.commands.section.view")}
      </button>
    </div>
  );
}

export function ProductCommandsSection({
  productId,
  token,
  data,
}: {
  productId: string;
  token: string;
  data: ProductCommandsResponse;
}) {
  const { t } = useI18n();
  const [category, setCategory] = React.useState<string>(ALL);
  const [query, setQuery] = React.useState("");
  const [sending, setSending] = React.useState<ProductCommand | null>(null);
  const [openExecutionId, setOpenExecutionId] = React.useState<string | null>(null);
  const { executions, inFlight, hasMore, loading, loadMore, track } = useProductCommandExecutions(
    productId,
    token,
    true,
  );

  const categories = React.useMemo(() => [...new Set(data.data.map((c) => c.category))], [data.data]);
  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.data
      .filter((c) => category === ALL || c.category === category)
      .filter(
        (c) =>
          !q ||
          c.displayName.toLowerCase().includes(q) ||
          c.key.toLowerCase().includes(q) ||
          (c.description ?? "").toLowerCase().includes(q),
      );
  }, [data.data, category, query]);
  const grouped = React.useMemo(() => {
    const map = new Map<string, ProductCommand[]>();
    for (const c of visible) map.set(c.category, [...(map.get(c.category) ?? []), c]);
    return [...map.entries()];
  }, [visible]);

  const openExecution = executions.find((e) => e.id === openExecutionId) ?? null;

  return (
    <div className={s.section}>
      {!data.device.connected && (
        <div className={s.bannerWarning}>
          <WifiOff size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{t("pro.commands.section.notConnectedBanner")}</span>
        </div>
      )}
      {data.device.isSimulated && (
        <div className={s.banner}>
          <Radio size={16} style={{ flexShrink: 0 }} />
          <span>{t("pro.commands.section.simulated")}</span>
        </div>
      )}
      {inFlight && <InFlightBanner execution={inFlight} onOpen={() => setOpenExecutionId(inFlight.id)} />}

      <div className={s.toolbar}>
        {categories.length > 1 && (
          <>
            <button
              type="button"
              className={`${s.chip}${category === ALL ? ` ${s.chipActive}` : ""}`}
              onClick={() => setCategory(ALL)}
            >
              {t("pro.commands.section.all")}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={`${s.chip}${category === c ? ` ${s.chipActive}` : ""}`}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </>
        )}
        <div className={s.search}>
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("pro.commands.section.search")}
            aria-label={t("pro.commands.section.search")}
          />
        </div>
      </div>

      {grouped.length === 0 && <span className={s.muted}>{t("pro.commands.section.noMatches")}</span>}
      {grouped.map(([group, commands]) => (
        <div key={group}>
          <div className={s.groupTitle}>{group}</div>
          <div className={s.grid}>
            {commands.map((command) => {
              const reason = disabledReason(t, command, data, inFlight);
              return (
                <button
                  key={command.id}
                  type="button"
                  className={s.commandCard}
                  disabled={Boolean(reason)}
                  title={reason ?? undefined}
                  onClick={() => setSending(command)}
                >
                  <div className={s.commandCardHeader}>
                    <span className={s.commandName}>{command.displayName}</span>
                    <CommandDangerPill level={command.dangerLevel} />
                  </div>
                  {command.description && <span className={s.muted}>{command.description}</span>}
                  {!command.available && command.unavailableReason && (
                    <span className={s.subtle}>{command.unavailableReason}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div>
        <div className={s.groupTitle}>{t("pro.commands.section.recent")}</div>
        {executions.length === 0 ? (
          <span className={s.muted}>
            {loading ? t("shared.common.loading") : t("pro.commands.section.noHistory")}
          </span>
        ) : (
          <div className={s.activityList}>
            {executions.map((execution) => (
              <button
                key={execution.id}
                type="button"
                className={s.activityRow}
                onClick={() => setOpenExecutionId(execution.id)}
              >
                <div style={{ minWidth: 0 }}>
                  <div className={s.commandName}>{execution.displayName ?? execution.commandKey}</div>
                  <div className={s.subtle}>
                    {[execution.actorName, commandSourceLabel(t, execution.source)].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span className={s.subtle}>{formatCommandTime(execution.createdAt)}</span>
                <CommandStatusPill status={execution.status} phase={execution.phase} />
              </button>
            ))}
          </div>
        )}
        {hasMore && (
          <div style={{ marginTop: 10 }}>
            <Button type="button" size="sm" variant="outline" color="neutral" onClick={loadMore} disabled={loading}>
              {t("pro.commands.section.loadMore")}
            </Button>
          </div>
        )}
      </div>

      <CommandSendModal
        command={sending}
        productId={productId}
        token={token}
        onClose={() => setSending(null)}
        onSent={(execution) => {
          track(execution);
          setSending(null);
          setOpenExecutionId(execution.id);
        }}
      />
      <CommandExecutionModal execution={openExecution} onClose={() => setOpenExecutionId(null)} />
    </div>
  );
}
